import React from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
} from '../components/site/SubPageParts';

const IMG = '/images/site/neurodivergent-explained';

// The six design principles, per QA_NuV_Neuro Expl.docx. (The mockup's grid
// still had placeholder duplicates in slots 3–6.)
const PRINCIPLES = [
  {
    title: 'Hyperfocus',
    body: 'Immerse into a task. Intense concentration and focused energy leading to significant breakthroughs and mastery of complex subjects, generating creative solutions.',
  },
  {
    title: 'Unconventional Thinking',
    body: 'Approach problems from unique angles, thinking outside the box. This unconventional perspective can result in creative solutions and innovative ideas that might not be considered by neurotypical individuals.',
  },
  {
    title: 'Visual-Spatial Reasoning',
    body: 'Visualize the problems and solutions in three dimensions.',
  },
  {
    title: 'Resilience and Adaptability',
    body: 'Design, develop and build unique strategies to overcome challenges, which can enhance problem-solving capabilities in high-pressure situations.',
  },
  {
    title: 'Direct Communication',
    body: 'Neurodivergent design is a fact-based approach. It is clear-eyed about the complexity of the problem framing and the solutions.',
  },
  {
    title: 'Creative Imagination',
    body: 'This creativity manifests in innovative product design, leading to unique solutions.',
  },
];

const TRAITS = [
  {
    title: 'Analytical',
    icon: `${IMG}/icon-analytical.png`,
    body: 'Problem identification, solving, critical thinking, and logical reasoning.',
  },
  {
    title: 'Creative',
    icon: `${IMG}/icon-creative.png`,
    body: 'Ability to generate novel ideas, think divergently, and adapt to new situations.',
  },
  {
    title: 'Practical',
    icon: `${IMG}/icon-practical.png`,
    body: 'Street smarts — applying knowledge effectively in everyday life.',
  },
];

/** Fan of arrows from the headline box down to the three trait columns. */
const FanArrows = () => (
  <svg
    viewBox="0 0 900 110"
    preserveAspectRatio="none"
    className="h-[70px] w-full md:h-[110px]"
    aria-hidden="true"
  >
    {[150, 450, 750].map((endX) => (
      <g key={endX} stroke="#9AA3AB" strokeWidth="2" fill="none">
        <line x1="450" y1="0" x2={endX} y2="92" />
        <polygon
          points={`${endX},104 ${endX - 7},88 ${endX + 7},88`}
          fill="#9AA3AB"
          stroke="none"
        />
      </g>
    ))}
  </svg>
);

/** Lines converging from the three trait columns back into the AI mark. */
const ConvergeLines = () => (
  <svg
    viewBox="0 0 900 110"
    preserveAspectRatio="none"
    className="h-[70px] w-full md:h-[110px]"
    aria-hidden="true"
  >
    {[150, 450, 750].map((startX) => (
      <line
        key={startX}
        x1={startX}
        y1="0"
        x2="450"
        y2="105"
        stroke="#9AA3AB"
        strokeWidth="2"
      />
    ))}
  </svg>
);

/** Heading over a two-column row: photograph on the left, copy on the right. */
const DesignSection = ({ heading, image, alt, children }) => (
  <PageSection className="pt-12">
    <SectionHeading>{heading}</SectionHeading>
    <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-10">
      <img
        src={image}
        alt={alt}
        className="h-[200px] w-full object-cover small:aspect-[23/10] small:h-auto"
      />
      <div className="space-y-4 text-[15px] font-light leading-snug text-black md:text-[18px]">
        {children}
      </div>
    </div>
  </PageSection>
);

const NeurodivergentExplained = () => (
  <SubPage title="Neurodivergent Explained">
    <PageTitleBox>Innovation meets results.</PageTitleBox>

    <PageSection className="pt-6">
      <img
        src={`${IMG}/think-different.jpeg`}
        alt="The words Think Different over a burst of coloured paint"
        className="mx-auto w-full max-w-[1200px] object-contain"
      />
    </PageSection>

    {/* ---------------- Two design philosophies ---------------- */}
    <PageSection className="pt-10">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16">
        <div>
          <h2 className="text-[22px] font-bold text-black md:text-[30px]">
            / Neurodivergent Designed.
          </h2>
          <div className="mt-3 h-px w-full bg-black/25" />
          <p className="mt-5 text-[15px] leading-relaxed text-black md:text-[17px]">
            This multi-dimensional problem-solving technique combines analytical
            reasoning with creative exploration and contextual awareness. This
            allows you to see how different parts of a problem set are connected.
          </p>
        </div>
        <div>
          <h2 className="text-[22px] font-bold text-black md:text-[30px]">
            / Neurotypical Designed.
          </h2>
          <div className="mt-3 h-px w-full bg-black/25" />
          <p className="mt-5 text-[15px] leading-relaxed text-black md:text-[17px]">
            Roughly 95% of software is neurotypical architected. This approach uses
            a single-dimensional principle ideally suited for straightforward,
            simple, easily understood problems. It struggles against diverse
            complex challenges, leading to poor design and results. It is limited
            in its capability to detect, understand and identify the
            interconnected patterns required to address the root cause of a
            dynamic problem.
          </p>
        </div>
      </div>
    </PageSection>

    {/* ---------------- The Art of the Solution ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>The Art of the Solution.</SectionHeading>
      {/* His face sits ~47% across. 46% centres it at every width -- within
          ~2% on phones and in the 14/5 frame above 600 -- so one anchor
          serves both; the old right-edge anchor pushed him to the left edge. */}
      <img
        src={`${IMG}/art-of-the-solution.jpeg`}
        alt="A man deep in thought against a chalkboard covered in question marks"
        className="mt-5 h-[280px] w-full object-cover object-[46%_50%] small:aspect-[14/5] small:h-auto"
      />
      {/* 8-27: sits under the photo now, not over it — no plate needed. */}
      <p className="mt-5 text-[20px] font-bold leading-snug text-black md:text-[28px]">
        Neurodivergent design employs a unique set of problem-solving
        techniques. It leverages 6 design principles that produce next-level
        solutions.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-7 md:grid-cols-3">
        {PRINCIPLES.map((principle, index) => (
          <div key={principle.title}>
            <h3 className="text-[17px] text-black md:text-[19px]">
              #{index + 1} {principle.title}:
            </h3>
            <p className="mt-1 text-[15px] leading-snug text-black md:text-[16px]">
              {principle.body}
            </p>
          </div>
        ))}
      </div>
    </PageSection>

    {/* ---------------- Rethinking the problem ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>Rethinking the Problem.</SectionHeading>
      <img
        src={`${IMG}/rethinking-the-problem.jpeg`}
        alt="A hand holding a blank white mask in the dark"
        className="mt-5 h-[220px] w-full object-cover small:aspect-[336/95] small:h-auto"
      />
    </PageSection>

    {/* 10-1: runs the full width — photo on the left, copy on the right. It
        used to share a row with a UniScam Design column, dropped 10-04. */}
    <DesignSection
      heading="AI = Analytical Intelligence."
      image={`${IMG}/analytical-intelligence.jpeg`}
      alt="A woman working at a laptop by a window"
    >
      <p>
        This layered scammer defense framework was created by the NuVola AI
        team. It focuses on digital signatures that cross behavioral tripwires
        indicating a high probability of at-risk interaction.
      </p>
      <p>
        This multipoint strategy interprets data interaction patterns and draws
        conclusions based off complex at-risk markers. This means it has the
        capability to detect, adapt to and understand scammer attack patterns.
      </p>
      <p>
        The end result is No / SCAM agent is a ScammerAware solution that
        understands a wide range of scammer threat patterns, delivering
        real-time scammer threat detection intelligence.
      </p>
    </DesignSection>

    {/* ---------------- Trait diagram ---------------- */}
    <PageSection className="pt-14">
      <div className="mx-auto w-full max-w-[900px]">
        <div className="rounded-[18px] border border-black/70 bg-[#DCE6F5] px-6 py-6 text-center">
          <p className="text-[22px] font-bold leading-tight text-black md:text-[34px]">
            Neurodivergent Designed = No / SCAM
          </p>
        </div>

        <FanArrows />

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
          {TRAITS.map((trait) => (
            <div key={trait.title} className="text-center">
              <h3 className="text-[19px] font-bold text-black md:text-[24px]">
                {trait.title}
              </h3>
              <img
                src={trait.icon}
                alt=""
                aria-hidden="true"
                className="mx-auto mt-3 h-[80px] w-auto object-contain md:h-[110px]"
              />
              {/* Below sm the columns stack, so the copy is held to the middle
                  two-thirds — the span between the fan's outer arrow tips
                  (x = 150 and 750 of 900) — instead of running edge to edge. */}
              <p className="mx-auto mt-3 w-2/3 text-[15px] leading-snug text-black sm:w-auto md:text-[16px]">
                {trait.body}
              </p>
            </div>
          ))}
        </div>

        <ConvergeLines />

        <div className="mx-auto flex w-[240px] items-center justify-center rounded-[18px] border border-black/25 py-6 md:w-[300px]">
          <img
            src={`${IMG}/ai-cloud-blue.png`}
            alt="NuVola AI"
            className="h-[60px] w-auto object-contain md:h-[80px]"
          />
        </div>
      </div>
    </PageSection>

    {/* ---------------- Closing ---------------- */}
    <PageSection className="pt-14">
      <img
        src={`${IMG}/paradigm-shift.jpeg`}
        alt="A man reclining at his desk with a cloud around his head"
        className="mx-auto h-[260px] w-full max-w-[1200px] object-cover small:aspect-[30/11] small:h-auto"
      />
      <p className="mt-6 text-center text-[26px] font-bold text-black md:text-[38px]">
        ParadigmShift
      </p>
    </PageSection>

    <ClosingBrand tagline="Innovation taken to the next level." />
  </SubPage>
);

export default NeurodivergentExplained;
